import { DeviceStats } from '@/features/devices/components/DeviceStats'
import { Block, Spinner, Text } from '@/ui-kit'
import { useUiKit } from '@/ui-kit/providers'
import { formatNumber } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Worker } from '../api/models'
import { useFetchWorkers } from '../api/useFetchWorkers'
import { DevicesList } from './DevicesList/DevicesList'
import { NoDevicesPlaceholder } from './NoDevicesPlaceholder'

const anchorId = 'devices-anchor'

export function Devices(): React.JSX.Element {
  const { t } = useLingui()
  const { data: workers = [], isFetched } = useFetchWorkers(true)
  const { isMobile } = useUiKit()
  const [selectedWorkerId, setSelectedWorkerId] = useState('')
  const selectedWorker: Worker | null = workers.find((worker) => worker.id === selectedWorkerId) ?? null

  const devicesColumnDefs = useMemo(
    () => [
      {
        name: t`Points 24h`,
        value: (worker: Worker) => formatNumber(worker.points24h, 0)
      }
    ],
    [t]
  )

  useEffect(() => {
    if (isFetched) {
      setSelectedWorkerId(workers.length ? workers[0].id : '')
    }
  }, [isFetched, workers])

  const onWorkerClick = useCallback(
    (id: string) => {
      setSelectedWorkerId(id)
      if (isMobile) {
        document.getElementById(anchorId)!.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    },
    [isMobile]
  )

  return isFetched && workers.length <= 0 ? (
    <NoDevicesPlaceholder />
  ) : (
    <>
      <Block className="max-mobile:mt-6 max-mobile:border-0 max-mobile:p-0 mt-5">
        <div className="flex items-start justify-between">
          <Text className="mr-4 pr-4" type="subtitle-semibold">
            <Trans>Devices</Trans>
          </Text>
        </div>
        <DevicesList
          columns={devicesColumnDefs}
          workers={workers}
          selectedWorkerId={selectedWorkerId}
          setSelectedWorkerId={onWorkerClick}
        />
      </Block>
      <div id={anchorId} className="flex flex-1 scroll-mt-[var(--header-height)]">
        {workers === undefined ? (
          <Spinner className="scaleInOutAnimation m-auto" />
        ) : selectedWorker ? (
          <DeviceStats worker={selectedWorker} />
        ) : (
          <Text className="text-gray-90 max-mobile:block hidden">
            <Trans>No data available yet</Trans>
          </Text>
        )}
      </div>
    </>
  )
}
